import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.jsx';
import './index.css';
// Initialize i18next for multi-language support
import { i18nReady } from './i18n/config';
import { initReloadTelemetry } from './utils/reloadTelemetry';

// Initialize Sentry for remote error tracking and console log capture
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' or 'production'

    // Capture 100% of transactions for debugging (reduce in production later)
    tracesSampleRate: 1.0,

    // debug.js adds its own structured breadcrumbs (category: "debug"),
    // so disable console.log/warn breadcrumbs to avoid duplicates.
    // Keep console.error enabled so ErrorBoundary and uncaught errors are captured.
    integrations: [
      Sentry.breadcrumbsIntegration({ console: true }),
      Sentry.browserTracingIntegration(),
      // Session Replay is intentionally DISABLED.
      //
      // It was added as a diagnostic for the "page refreshes and deletes my
      // data" checkout reload. That reload is now understood (iOS Safari
      // discarding the heavy checkout tab under memory pressure — see
      // reloadTelemetry.js), so the replay has served its purpose.
      //
      // Crucially, on-error replay does NOT just sample uploads: rrweb records
      // CONTINUOUSLY into an in-memory buffer so it can retroactively send the
      // seconds before an error. On a low-RAM iPhone that buffer adds to the
      // very memory pressure that triggers the reload — i.e. the diagnostic was
      // worsening the bug it was watching. Removing the integration (not merely
      // lowering replaysOnErrorSampleRate) is what actually frees that memory.
      //
      // To re-enable for a short, scoped diagnostic window, restore:
      //   Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      // and set replaysOnErrorSampleRate below back to a non-zero value.
    ],

    // Filter out duplicate console.log/warn breadcrumbs (debug.js already
    // adds these as category:"debug"). Only keep console.error breadcrumbs.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null; // drop console.log/warn duplicates
      }
      return breadcrumb;
    },

    // Drop noisy network errors that aren't actionable.
    // iOS "Load failed" / Chrome "Failed to fetch" are transient connectivity
    // issues. Minified PostgREST class names (single short token like "Nd")
    // are the same errors without a readable message.
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || '';
      // Always let payment-flow events through — even if the underlying
      // cause looks like a transient network error, we need visibility
      // into payment failures to support affected users.
      if (event.tags?.flow === 'payment') {
        return event;
      }
      // iOS network error or Chrome network error
      if (/Load failed|Failed to fetch|NetworkError/i.test(message)) {
        return null;
      }
      // Minified PostgREST error class name (1-3 chars, no spaces)
      // These are network errors where the message is just the mangled class name
      if (/^[A-Za-z]{1,3}$/.test(message)) {
        return null;
      }
      // Third-party browser errors (DuckDuckGo, Brave, etc.) that inject
      // scripts into the page. Not from our code, not actionable.
      if (/feature named `.+` was not found/.test(message)) {
        return null;
      }
      return event;
    },

    // 300 breadcrumbs covers a full booking session's UI/network trail. This
    // was previously 2000 to capture the heavy category:"debug" breadcrumbs
    // during the checkout-reload investigation; with the merchant `debug` flag
    // off those are no longer written, so the buffer no longer needs to be that
    // large. Smaller buffer = less retained memory on low-RAM devices.
    maxBreadcrumbs: 300,

    // Send user agent, useful for Android/Chrome debugging
    sendDefaultPii: false,

    // Session Replay sampling — both 0 because the replayIntegration above is
    // disabled. Kept here (rather than deleted) so re-enabling for a scoped
    // diagnostic window is a one-line change. Note: with the integration
    // removed these are inert; a non-zero value here does nothing on its own.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });

  // Detect full-page reloads / tab restores that land on a booking step and
  // report them to Sentry (PII-free). This is what makes the customer's
  // "page refreshes and deletes my data" complaint visible.
  initReloadTelemetry();
}

// Wait for the detected language's translations to load before the first render
// so non-English users don't see a flash of the English fallback. Resolves
// immediately for English users (and on load failure, falls back to English).
i18nReady.finally(() => {
  createRoot(document.getElementById('root')).render(
  <StrictMode>
      <App />
  </StrictMode>
  );
});